// Part 1a rows: the manual-mode adapter. Today's manual form state (24 manual* variables in
// AppProvider, form-bound until 1b) becomes rows at Run through manualFormToRows, so the form
// is no longer a dispatch source. Rows are assembled by buildRow, the same assembler the
// router path uses, followed by the form's overrides.
//
// Manual values are expert values: manualFormToRows never calls applySteering (D33), so 2a's
// steering never overwrites them.
//
// Pure and deterministic: no React, no I/O. Never mutates its input.
import { variantConditioningProperty } from '../router/modelRouter';
import { normalizeModelPolicy } from '../../config/capabilities';
import { PLAN_LIMITS } from '../../config/planLimits';
import { GT4SD_CHECKPOINTS, gt4sdTargetToken } from '../../config/gt4sdCheckpoints';
import { customMattergenCheckpoint } from '../../config/mattergenConditioning';
import { MATTERGEN_BUILTIN_CHECKPOINTS } from '../../components/mattergen/mattergenCheckpoints';
import { normalizeMattergenChunkSize } from '../../components/mattergen/mattergenJobs';
import { selectCustomModel } from '../../context/customModelSelection';
import { fm4mSeedSmiles } from '../../utils/generationBounds';
import { MAX_ROW_CANDIDATES, FM4M_REPRESENTATION_MODELS, FM4M_FEATURIZE_CHUNK, GT4SD_ALGORITHMS } from './rowContract';
import { buildRow, rowSeedSmiles } from './prefillRows';

const own = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

// Fixed ids by service (1a-contracts 1.1): MatterGen m1, FM4M m2, GT4SD m3.
const MANUAL_ROW_IDS = Object.freeze({ mattergen: 'm1', fm4m: 'm2', gt4sd: 'm3' });
// The properties the manual MatterGen form offers (MattergenForm.jsx).
const MANUAL_PROPERTIES = Object.freeze(['energy_above_hull', 'band_gap', 'bulk_modulus']);
// The largest MatterGen chunk the 1a ladder and row validation allow.
const MATTERGEN_MAX_CHUNK = 256;
const DEFAULT_NUM_CANDIDATES = 64;
const DEFAULT_GT4SD_SAMPLES = 10;
const DEFAULT_MAX_ITEMS_INLINE = 10;
const MAX_ITEMS_INLINE = 1000;
const MAX_TARGET_SMILES_LENGTH = 1000;

/** D39 count rule: n = floor(Number(v)); not finite or below 1 takes the field default; then
 * clamped into 1 to the plan max (and never above MAX_ROW_CANDIDATES). Form fields hold
 * strings, so '64.5' gives 64 and 'abc', '' or NaN give the default. */
function manualCount(v, fallback, planMax) {
  let n = Math.floor(Number(v));
  if (!Number.isFinite(n) || n < 1) n = fallback;
  return Math.max(1, Math.min(n, planMax, MAX_ROW_CANDIDATES));
}

/** A form number that counts only when finite; '', null and undefined are no value. */
function finiteOrNull(v) {
  if (v === '' || v === null || v === undefined || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function guidanceOrNull(v) {
  const g = finiteOrNull(v);
  return g !== null && g >= 0.1 && g <= 10 ? g : null;
}

function stringList(v) {
  return (Array.isArray(v) ? v : [])
    .filter((s) => typeof s === 'string')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The FM4M alias rule (bart, mhg, smi-ted and unknown names become mol-xl). */
function fm4mModelOrDefault(name) {
  return FM4M_REPRESENTATION_MODELS.includes(name) ? name : 'mol-xl';
}

/** The custom models auto-routing may offer: families the org policy runs (settled decision 5). */
function runnableCustomModels(customModels, modelPolicy) {
  const policy = normalizeModelPolicy(modelPolicy);
  return (Array.isArray(customModels) ? customModels : []).filter((m) => {
    const entry = m && typeof m.family === 'string' && own(policy, m.family) ? policy[m.family] : null;
    return !!entry && entry.enabled && entry.weight > 0;
  });
}

/** Today's manual auto chain (formerly resolvePrimaryObjective + PRIMARY_TO_VARIANT); the
 * property counts only with a finite value. */
function autoCheckpoint(property, value, hasElements) {
  if (value !== null && property === 'energy_above_hull' && hasElements) return 'chemical_system_energy_above_hull';
  if (value !== null && property === 'band_gap') return 'dft_band_gap';
  if (value !== null && property === 'bulk_modulus') return 'ml_bulk_modulus';
  if (hasElements) return 'chemical_system';
  return 'mattergen_base';
}

function mattergenManualRow(form, spec, ctx) {
  const mg = isPlainObject(form.mattergen) ? form.mattergen : {};
  const property = MANUAL_PROPERTIES.includes(form.property) ? form.property : '';
  const value = finiteOrNull(form.propertyValue);
  const explicit = typeof mg.checkpoint === 'string' ? mg.checkpoint.trim() : '';
  let target = MATTERGEN_BUILTIN_CHECKPOINTS.includes(explicit)
    ? explicit
    : autoCheckpoint(property, value, spec.system.elements_in.length > 0);
  let customModelId = null;
  const pick = typeof mg.customModelId === 'string' && mg.customModelId ? mg.customModelId : null;
  if (pick) {
    // Explicit pick. A pick that is no longer a verified MatterGen model (a stale Refine pick)
    // keeps its id and the form's checkpoint; prepareRows (1a-03) rejects it as
    // 'custom_model_unavailable', never re-routing it to a built-in (D45).
    customModelId = pick;
    const model = ctx.customModels.find((m) => m && m.id === pick && m.family === 'mattergen');
    if (model) target = customMattergenCheckpoint(model);
  } else {
    const offered = runnableCustomModels(ctx.customModels, ctx.modelPolicy);
    const auto = selectCustomModel({
      customModels: offered,
      family: 'mattergen',
      property: variantConditioningProperty('mattergen', target),
      familyConditions: true,
    });
    if (auto) {
      customModelId = auto;
      target = customMattergenCheckpoint(offered.find((m) => m.id === auto));
    }
  }
  const row = buildRow({
    id: MANUAL_ROW_IDS.mattergen,
    service: 'mattergen',
    kind: 'generate',
    domain: 'inorganic_crystal',
    target,
    customModelId,
    candidates: manualCount(form.numCandidates, DEFAULT_NUM_CANDIDATES, ctx.planMax),
    origin: 'expert',
  }, spec);
  const guidance = guidanceOrNull(mg.guidance);
  if (guidance !== null) row.params.diffusion_guidance_factor = guidance;
  row.params.record_trajectories = typeof mg.recordTrajectories === 'boolean' ? mg.recordTrajectories : true;
  row.chunkSize = Math.min(MATTERGEN_MAX_CHUNK, normalizeMattergenChunkSize(mg.chunkSize));
  return row;
}

function fm4mManualRow(form, spec, ctx) {
  const fm = isPlainObject(form.fm4m) ? form.fm4m : {};
  const domain = fm.category === 'polymer' ? 'polymer' : 'small_molecule';
  const model = fm4mModelOrDefault(fm.model);
  const returnMode = fm.returnMode === 'inline' ? 'inline' : 'file';
  let chunk = Math.floor(Number(fm.chunkSize));
  if (!Number.isFinite(chunk) || chunk < FM4M_FEATURIZE_CHUNK.min || chunk > FM4M_FEATURIZE_CHUNK.max) chunk = FM4M_FEATURIZE_CHUNK.default;
  const seeds = rowSeedSmiles(fm.seeds);
  let row;
  if (seeds.length > 0) {
    row = buildRow({
      id: MANUAL_ROW_IDS.fm4m, service: 'fm4m', kind: 'represent', domain, target: model,
      customModelId: null, candidates: seeds.length, origin: 'expert', seedSmiles: fm.seeds,
    }, spec);
  } else {
    const offered = runnableCustomModels(ctx.customModels, ctx.modelPolicy);
    row = buildRow({
      id: MANUAL_ROW_IDS.fm4m, service: 'fm4m', kind: 'generate', domain, target: 'bart',
      customModelId: selectCustomModel({ customModels: offered, family: 'fm4m', familyConditions: false }),
      candidates: manualCount(form.numCandidates, DEFAULT_NUM_CANDIDATES, ctx.planMax), origin: 'expert',
    }, spec);
    // Manual generation is unfiltered: the manual target is a crystal property ('=').
    row.params.filters = {};
    row.params.oversample_multiplier = 1;
    row.params.featurization_model = model;
  }
  row.params.return_mode = returnMode;
  if (returnMode === 'inline') {
    let max = Math.floor(Number(fm.maxItemsInline));
    if (!Number.isFinite(max) || max < 1 || max > MAX_ITEMS_INLINE) max = DEFAULT_MAX_ITEMS_INLINE;
    row.params.max_items_inline = max;
  }
  row.chunkSize = chunk;
  return row;
}

function gt4sdManualRow(form, spec, ctx) {
  const gt = isPlainObject(form.gt4sd) ? form.gt4sd : {};
  // Part D: regression_transformer is the only algorithm with a built-in checkpoint
  // (RT-6), so it is the fallback and the manual form's default.
  const algorithm = GT4SD_ALGORITHMS.includes(gt.algorithm) ? gt.algorithm : 'regression_transformer';
  const offered = runnableCustomModels(ctx.customModels, ctx.modelPolicy);
  const row = buildRow({
    id: MANUAL_ROW_IDS.gt4sd, service: 'gt4sd', kind: 'generate', domain: 'small_molecule', target: algorithm,
    customModelId: selectCustomModel({ customModels: offered, family: 'gt4sd', familyConditions: false }),
    candidates: manualCount(gt.numSamples, DEFAULT_GT4SD_SAMPLES, ctx.planMax), origin: 'expert',
  }, spec);
  const versions = GT4SD_CHECKPOINTS[algorithm];
  const allowlisted = Array.isArray(versions) && versions.includes(gt.algorithmVersion);
  // buildRow wrote the default version, or none when the live list is empty (1.1 exception).
  const version = allowlisted ? gt.algorithmVersion : row.params.algorithm_version;
  const params = version === undefined ? {} : { algorithm_version: version };
  // RT fields only on RT rows: GT4SDForm's algorithm switch clears only the version, so RT
  // values left behind under hugging_face are ignored, not sent.
  if (algorithm === 'regression_transformer') {
    const smiles = typeof gt.targetSmiles === 'string' ? gt.targetSmiles.trim() : '';
    if (smiles.length > 0 && smiles.length <= MAX_TARGET_SMILES_LENGTH) params.target_smiles = smiles;
    // Each half is written on its own, so an incomplete pair is rejected by name on the
    // half that is MISSING. Dropping both named target_value even when the user had just
    // typed it, which pointed them at the wrong field (the Target Property select has one
    // real option and opens unselected). A value that is not a known token is still
    // dropped: an unknown property would encode as [UNK] with no error.
    const token = typeof gt.targetProperty === 'string' && gt.targetProperty ? gt4sdTargetToken(gt.targetProperty, version) : null;
    const value = finiteOrNull(gt.targetValue);
    if (token) params.target_property = token;
    if (value !== null) params.target_value = value;
  }
  row.params = params;
  return row;
}

/** The manual spec: today's manual spec (AppProvider.jsx at 7f0c897) without any *_options or
 * seed_smiles, with num_candidates clamped to the plan max. As today, a picked property with
 * an entered value gives the one target, whose value is null when the entry is not a number. */
function manualSpec(form, planMax) {
  const models = ['mattergen', 'fm4m', 'gt4sd'].filter((m) => form.models[m] === true);
  const category = form.fm4m?.category === 'polymer' ? 'polymer' : 'small_molecule';
  const family = [];
  if (models.includes('mattergen')) family.push('inorganic_crystal');
  if (models.includes('fm4m')) family.push(category);
  if (models.includes('gt4sd')) family.push('small_molecule');
  const property = MANUAL_PROPERTIES.includes(form.property) ? form.property : '';
  const raw = form.propertyValue;
  const entered = raw !== '' && raw !== null && raw !== undefined;
  const targets = property && entered
    ? [{ name: property, type: 'other', constraint: '=', value: finiteOrNull(raw), unit: null, priority: 'must', source: 'explicit' }]
    : [];
  return {
    id: null,
    mode: 'materials',
    models,
    system: {
      family,
      elements_in: stringList(form.elementsIn),
      elements_out: stringList(form.elementsOut),
      max_elements: null,
      dimensionality: typeof form.dimensionality === 'string' && form.dimensionality ? form.dimensionality : 'unknown',
      structure_hint: stringList(form.structureHints),
    },
    targets,
    conditions: { temp_range_K: null, pressure_range_bar: null, environment: [], notes: '' },
    constraints: {
      toxicity: 'unspecified',
      criticality: 'unspecified',
      cost_tier: 'unspecified',
      num_candidates: manualCount(form.numCandidates, DEFAULT_NUM_CANDIDATES, planMax),
      patent_novelty_bias: 'unspecified',
    },
    meta: { user_prompt_raw: '', created_by: 'manual' },
  };
}

/**
 * Manual form -> rows, at Run (1a-contracts 2.3). One row per ticked model, ids m1 (MatterGen),
 * m2 (FM4M) and m3 (GT4SD). Every form value is normalized into the 1a bounds here (D39),
 * because onSubmit never checks hasManualErrors and legacy specs carry off-menu values; so
 * every row validates (builder invariant), except a GT4SD row whose live checkpoint list is
 * empty (1.1). Does not filter by policy (the form masks ticks; prepareRows is the backstop).
 * @param {ManualForm} form  plain data (numeric fields may be raw form strings)
 * @param {{ customModels, modelPolicy, planMax }} ctx  planMax missing -> the pilot max
 * @returns {{ rows: Row[], spec: object }}
 */
export function manualFormToRows(form, ctx = {}) {
  const f = isPlainObject(form) ? form : {};
  const input = { ...f, models: isPlainObject(f.models) ? f.models : {} };
  const c = isPlainObject(ctx) ? ctx : {};
  const planMax = Number(c.planMax) || PLAN_LIMITS.pilot.maxCandidatesPerRun;
  const context = {
    customModels: Array.isArray(c.customModels) ? c.customModels : [],
    modelPolicy: c.modelPolicy,
    planMax,
  };
  const spec = manualSpec(input, planMax);
  const rows = [];
  if (input.models.mattergen === true) rows.push(mattergenManualRow(input, spec, context));
  if (input.models.fm4m === true) rows.push(fm4mManualRow(input, spec, context));
  if (input.models.gt4sd === true) rows.push(gt4sdManualRow(input, spec, context));
  return { rows, spec };
}

/**
 * A stored manual spec -> the ManualForm it was built from (legacy manual plans, 1.11). A
 * plain mapping of spec fields onto the form shape: seeds go through fm4mSeedSmiles (a string
 * becomes a one-element list); every bound is left to manualFormToRows.
 * @param {object} spec  generation_spec with meta.created_by === 'manual'
 * @param {{ mgCustomModelId?: string|null }} opts  the explicit MatterGen custom pick, if any
 * @returns {ManualForm}
 */
export function manualFormFromSpec(spec, { mgCustomModelId = null } = {}) {
  const s = isPlainObject(spec) ? spec : {};
  const models = Array.isArray(s.models) ? s.models : [];
  const system = isPlainObject(s.system) ? s.system : {};
  const family = Array.isArray(system.family) ? system.family : [];
  const first = Array.isArray(s.targets) && isPlainObject(s.targets[0]) ? s.targets[0] : null;
  const meta = isPlainObject(s.meta) ? s.meta : {};
  const mg = isPlainObject(meta.mattergen_options) ? meta.mattergen_options : {};
  const fm = isPlainObject(meta.fm4m_options) ? meta.fm4m_options : {};
  const gt = isPlainObject(meta.gt4sd_options) ? meta.gt4sd_options : {};
  return {
    models: { mattergen: models.includes('mattergen'), fm4m: models.includes('fm4m'), gt4sd: models.includes('gt4sd') },
    elementsIn: Array.isArray(system.elements_in) ? system.elements_in : [],
    elementsOut: Array.isArray(system.elements_out) ? system.elements_out : [],
    dimensionality: typeof system.dimensionality === 'string' ? system.dimensionality : 'unknown',
    structureHints: Array.isArray(system.structure_hint) ? system.structure_hint : [],
    property: first && typeof first.name === 'string' ? first.name : '',
    propertyValue: first ? (first.value ?? null) : null,
    numCandidates: s.constraints?.num_candidates,
    mattergen: {
      checkpoint: typeof mg.pretrained_override === 'string' ? mg.pretrained_override : '',
      guidance: mg.diffusion_guidance_factor ?? null,
      recordTrajectories: typeof mg.record_trajectories === 'boolean' ? mg.record_trajectories : true,
      chunkSize: mg.chunk_size,
      customModelId: mgCustomModelId || null,
    },
    fm4m: {
      category: family.includes('polymer') ? 'polymer' : 'small_molecule',
      seeds: fm4mSeedSmiles(meta),
      model: typeof fm.model === 'string' ? fm.model : 'mol-xl',
      returnMode: fm.return_mode,
      maxItemsInline: fm.max_items_inline ?? null,
      chunkSize: fm.chunk_size,
    },
    gt4sd: {
      algorithm: gt.algorithm,
      algorithmVersion: typeof gt.algorithm_version === 'string' ? gt.algorithm_version : '',
      targetSmiles: typeof gt.target_smiles === 'string' ? gt.target_smiles : '',
      targetProperty: typeof gt.target_property === 'string' ? gt.target_property : '',
      targetValue: gt.target_value ?? null,
      numSamples: gt.n_samples,
    },
  };
}
